import { Checkbox, FormControlLabel } from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { Api } from "../../../../../../common/service/Api";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { SUBMIT_FIELD_NAMES } from "../../../DataRequestSubjectCard";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useOrganizationVendors } from "../../../../../hooks";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  BUSINESS_SURVEY_NAME,
  KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG,
} from "../../../../../surveys/businessSurvey";
import {
  filterLimitVendors,
  filterSensitivePICs,
  getCollectedPicsFromCollectionGroupsWithSensitiveInferencesPIC,
} from "./ContactVendors";
import {
  isGoogleAnalytics,
  isRequestContextApplicableWithCrunchFiltering,
  isRequestRegionApplicable,
} from "../../../../../util/vendors";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { InstructionsContent } from "../shared/CollapsibleInstructions";
import { getAllInformationForLimitRequests } from "../../../../../pages/requestInstructions/LimitInstructionsPage";
import { Button } from "@truevault/aquilo";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { Link } from "react-router-dom";
import { LoadingButton } from "../../../../../../common/components/LoadingButton";
import { DataRequestCustomFieldDisplay } from "../../DataRequestCustomFieldDisplay";

export const LimitDataStep = () => {
  const [org] = usePrimaryOrganization();
  const [allVendors] = useOrganizationVendors(org?.id, undefined, true);
  const { data: dataMap } = useDataMap(org.id);

  const { answers } = useSurvey(org?.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { answers: bizSurveyAnswers } = useSurvey(org?.id, BUSINESS_SURVEY_NAME);

  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();
  const isEmployee = request.context == "EMPLOYEE";
  const collectedCategories = getCollectedPicsFromCollectionGroupsWithSensitiveInferencesPIC(
    dataMap,
    isEmployee,
  );

  const {
    vendorIdsForProcessingLimitRequests,
    shouldUseLegacyLimitVendors,
    processedSensitivePICIds,
  } = getAllInformationForLimitRequests(allVendors, dataMap);

  const childrenUnder16DataCollectionEnabled =
    bizSurveyAnswers?.[KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG] === "true";

  // Get vendor IDs that are disclosed ANY data from non-employment collection groups
  // These are relevant for children under 16 SPI since any vendor receiving consumer data could be processing children's data
  const vendorIdsWithNonEmploymentDisclosures = new Set<string>();
  dataMap?.disclosure?.forEach((disclosure) => {
    const collectionGroup = dataMap.collectionGroups[disclosure.groupId];
    if (collectionGroup && collectionGroup.type !== "EMPLOYMENT") {
      vendorIdsWithNonEmploymentDisclosures.add(disclosure.vendorId);
    }
  });

  // Include vendors from PIC-based limit logic, plus (if children under 16) vendors with non-employment disclosures that answered "Yes"
  let newLimitVendors = (allVendors || [])
    .filter((v) => {
      const isFromPicBasedLimit = (vendorIdsForProcessingLimitRequests || []).includes(v.vendorId);
      const isFromChildrenUnder16 =
        childrenUnder16DataCollectionEnabled &&
        vendorIdsWithNonEmploymentDisclosures.has(v.vendorId) &&
        v.spiUsedForNonEssentialPurposes === true;
      return isFromPicBasedLimit || isFromChildrenUnder16;
    })
    .filter((v) =>
      isRequestContextApplicableWithCrunchFiltering(dataMap, request.context, v, request, org),
    );
  let newVendorSensitivePICs = dataMap
    ? processedSensitivePICIds.map((id) => dataMap.categories[id])
    : [];

  const shouldUseNewLimitCriteria =
    allVendors && allVendors.length > 0 && dataMap && !shouldUseLegacyLimitVendors;

  const oldLimitVendors = filterLimitVendors(allVendors, dataMap, answers, request.context)
    ?.filter(
      (v) =>
        !v.isInstalled ||
        (request.dataRequestType.regulationLaw.slug === "GDPR" &&
          v.gdprProcessorSetting === "Processor"),
    )
    ?.filter(
      (v) =>
        isRequestContextApplicableWithCrunchFiltering(dataMap, request.context, v, request, org) &&
        isRequestRegionApplicable(request, v),
    );
  const limitVendors = shouldUseNewLimitCriteria ? newLimitVendors : oldLimitVendors;
  const sortedVendors = sortVendorsByCategory(limitVendors);

  const oldSensitivePI = filterSensitivePICs(dataMap, collectedCategories);
  const sensitivePI = shouldUseNewLimitCriteria ? newVendorSensitivePICs : oldSensitivePI;

  const allSensitivePI = [
    ...sensitivePI,
    ...(childrenUnder16DataCollectionEnabled
      ? [{ id: "children-under-16-survey", name: "Data from children under 16" }]
      : []),
  ];

  const api = useCallback(
    ({ request, finished }) => {
      const existingValues = Object.fromEntries(
        Object.entries(request).filter(([k]) => SUBMIT_FIELD_NAMES.includes(k)),
      );
      const values = Object.assign({}, existingValues, { limitationFinished: finished }) as any;
      return Api.dataRequest.updateRequest(org.id, request.id, values);
    },
    [request, org.id],
  );

  const [isAttemptingAutomation, setIsAttemptingAutomation] = React.useState(false);
  const submitRequestForAutomation = useCallback(async (orgId, requestId) => {
    return Api.dataRequest.manuallyAutomateRequest(orgId, requestId);
  }, []);

  const getAutomationCallout = (
    automationVendorResult: string,
    vendor: OrganizationDataRecipientDto,
    request: DataSubjectRequestDetailDto,
  ) => {
    const googleAnalyticsFailureInfo = request?.hasGaUserId ? (
      <>
        <div>
          Check that Google Analytics is fully set up on your{" "}
          <Link className="text-weight-regular" to="/privacy-center/integrations">
            Integrations
          </Link>{" "}
          page. After setting it up, you may retry the automatic delete.
        </div>
        {request?.batchPublicId === null && (
          <LoadingButton
            variant="contained"
            className="manually-automate-request-button mt-xs"
            loading={isAttemptingAutomation}
            onClick={async () => {
              setIsAttemptingAutomation(true);
              await submitRequestForAutomation(org.id, request.id);
              setIsAttemptingAutomation(false);
              refreshRequest();
            }}
          >
            <img src="/assets/images/automation-icon.svg" alt="" />️ Retry
          </LoadingButton>
        )}
      </>
    ) : (
      <>
        This request did not include a consumer's Google Analytics User ID, so data could not be
        limited automatically.
      </>
    );

    switch (automationVendorResult) {
      case "AUTOMATION_REQUESTED":
        return (
          <Callout
            variant={CalloutVariant.Green}
            title="Request to limit data automatically sent"
          ></Callout>
        );
      case "AUTOMATION_CONFIRMED":
        return (
          <Callout variant={CalloutVariant.Green} title="Data automatically limited"></Callout>
        );
      case "AUTOMATION_NO_DATA":
        return (
          <Callout
            variant={CalloutVariant.Secondary}
            title="Vendor indicated no data to limit for the consumer"
          ></Callout>
        );
      case "AUTOMATION_FAILED":
        return (
          <Callout
            variant={CalloutVariant.Yellow}
            title="Automatic limiting not successful"
            subtitle={isGoogleAnalytics(vendor) ? <>{googleAnalyticsFailureInfo}</> : <></>}
          ></Callout>
        );
      default:
        return null;
    }
  };

  const { fetch: updateField } = useActionRequest({ api });

  const vendorOutcomes = useMemo(() => {
    const outcomes = request.vendorOutcomes.reduce(
      (acc, outcome) => Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome }),
      {},
    );

    request._local?.pendingVendorOutcomeUpdates?.forEach((pending) => {
      outcomes[pending.vendorId] = pending.outcome;
    });

    return outcomes;
  }, [request.vendorOutcomes, request._local?.pendingVendorOutcomeUpdates]);

  const handleSelectionUpdated = async (isFinished: boolean) => {
    await updateField({ request, finished: isFinished });
    refreshRequest();
  };

  useEffect(() => {
    let isCompleted = false;
    if (request.limitationFinished) {
      isCompleted = true;
    } else if (request.consumerGroupResult === "PROCESSED_AS_SERVICE_PROVIDER") {
      isCompleted = true;
    }
    setStepCompleted(isCompleted);
  }, [request.consumerGroupResult, request.limitationFinished, setStepCompleted]);

  if (request.consumerGroupResult === "PROCESSED_AS_SERVICE_PROVIDER") {
    return (
      <>
        <div className="text-t3 mb-std">
          You have marked this request as being processed as a service provider. You may proceed to
          the next step to notify the consumer with information on how to exercise their rights.
        </div>
        <div className="tw-boundary">
          <Button
            color="tertiary"
            onClick={async () => {
              await Api.dataRequest.setSubjectStatus(org.id, request.id, {
                subjectStatus: "FOUND",
              });
              refreshRequest();
            }}
          >
            Undo Process as Service Provider
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <DataRequestCustomFieldDisplay org={org} request={request} classList="mb-std" />

      <div className="text-t3 mb-std">
        Your business is required to stop using this consumer's Sensitive Personal Information for
        any purposes other than providing products or services requested by the consumer, and for
        internal record-keeping purposes, such as to fulfill legal and compliance obligations. If
        you make inferences from Sensitive Personal Information, you are obligated to stop
        collecting and storing such information about this consumer. Check the box below when you've
        made any necessary changes to your data processing about this consumer.
      </div>

      <Callout
        variant={CalloutVariant.Clear}
        title="Based on your Data Map, your business handles the following Sensitive Personal Information:"
        className="mb-md"
      >
        <ul>
          {allSensitivePI.map((pi) => (
            <li key={pi.id}>{pi.name}</li>
          ))}
        </ul>
      </Callout>

      <FormControlLabel
        control={
          <Checkbox
            checked={request.limitationFinished ?? false}
            onChange={({ target }) => handleSelectionUpdated(target.checked)}
          />
        }
        label="We've limited our use of this consumer's Sensitive Personal Information to uses that are necessary to provide products or services to the consumer, or for legal/compliance obligations."
      />

      <div className="mt-xl">
        {sortedVendors && (
          <CategorizedVendorList
            vendors={sortedVendors}
            renderVendor={(vendor) => {
              const limitInstruction = vendor.instructions.find((vi) => vi.requestType == "LIMIT");
              const limitOutcome = vendorOutcomes ? vendorOutcomes[vendor.vendorId] : null;

              return (
                <div key={`vendor-${vendor.id}`} className={"vendor-data-container"}>
                  <hr />
                  <div className={"flex-container pt-std pb-std border-top--grey vendor-data-card"}>
                    <div className="flex-grow vendor-data-vanity">
                      <div className="text-t4 w-256 flex-container flex--align-start pl-xs">
                        <DataRecipientLogo dataRecipient={vendor} size={32} />
                        <div className="ml-mdlg">{vendor.name}</div>
                      </div>
                    </div>
                    <div className="flex-grow" style={{ marginLeft: "100px" }}>
                      {getAutomationCallout(limitOutcome, vendor, request)}
                      <InstructionsContent
                        processingInstructions={limitInstruction?.processingInstructions}
                        title="Limit Instructions"
                        isExpanded={true}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        )}
      </div>
    </>
  );
};
